import { ResponsiveTable } from '../components/common/ResponsiveTable';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../config/api';
import { useThemedStyles } from '../theme/useThemedStyles';
import { invalidateAppointmentCache } from '../utils/appointmentCache';
import { ModalBackdrop } from '../components/common/ModalBackdrop';
import { formatDate } from '../utils/dateFormat';
import { BolivianDateInput } from '../components/common/BolivianDateInput';
import { ReportAppointmentDetails } from '../components/reports/ReportAppointmentDetails';

interface Capabilities {
  canEditClinical: boolean;
  canEditScheduling: boolean;
  canEditPayments: boolean;
  canOverrideCalculated: boolean;
}

interface ReportPayment {
  id: number;
  date: string;
  amount: number;
  method: string;
  notes: string;
  kind: string;
  inSelectedPeriod: boolean;
}

interface ReportOverride {
  commissionableAmount: number;
  primaryDoctorShare: number;
  secondDoctorShare: number;
  clinicShare: number;
}

interface AppointmentReportLine {
  balanceDue?: number | null;
  lineKey: string;
  appointmentId: number;
  treatmentId?: number | null;
  appointmentDate: string;
  patientId: number;
  patientHc?: string | null;
  patientName: string;
  treatmentName: string;
  treatedTeeth?: string | null;
  grossAmount: number;
  discountAmount: number;
  invoice: boolean;
  total: number;
  periodPaid: number;
  paymentMethods: string;
  specialMaterialsCost: number;
  category: 'general' | 'specialty';
  laboratoryCost: number;
  laboratoryId?: number | null;
  laboratoryName?: string | null;
  mechanicName?: string | null;
  commissionableAmount: number;
  doctorId?: number | null;
  doctorName?: string | null;
  secondDoctorId?: number | null;
  secondDoctorName?: string | null;
  commissionPercentage: number;
  commissionOverride?: number | null;
  secondDoctorCommissionPercentage: number;
  primaryDoctorShare: number;
  secondDoctorShare: number;
  clinicShare: number;
  override?: ReportOverride | null;
  payments: ReportPayment[];
  capabilities: Capabilities;
}

interface ReportResponse {
  lines: AppointmentReportLine[];
  capabilities: Capabilities;
}

interface PatientOption {
  id: number;
  hc?: string | null;
  HC?: string | null;
  presentedName?: string;
  name?: string;
  firstName?: string;
  firstLastName?: string;
}

interface DoctorOption { id: number; firstName: string; lastName: string }
interface LaboratoryOption { id: number; name: string }

interface EditablePayment extends Omit<ReportPayment, 'id'> { id?: number }

interface EditState {
  appointmentDate: string;
  patientId: string;
  doctorId: string;
  secondDoctorId: string;
  treatmentName: string;
  treatedTeeth: string;
  grossAmount: string;
  discountAmount: string;
  total: string;
  invoice: boolean;
  specialMaterialsCost: string;
  category: 'general' | 'specialty';
  laboratoryCost: string;
  laboratoryId: string;
  mechanicName: string;
  commissionOverride: string;
  secondDoctorCommissionPercentage: string;
  payments: EditablePayment[];
  useCalculatedOverride: boolean;
  overrideEntry: string;
  overridePrimary: string;
  overrideSecond: string;
  overrideClinic: string;
}

interface DoctorTreatmentsPageProps {
  doctorId: number;
  doctorName: string;
  month: string;
  onBack: () => void;
}

const money = (value: number) => `Bs ${Number(value || 0).toFixed(2)}`;
const inputDate = (value: string) => value ? value.slice(0, 10) : '';
const patientLabel = (patient: PatientOption) =>
  patient.presentedName || patient.name || `${patient.firstName || ''} ${patient.firstLastName || ''}`.trim() || `#${patient.id}`;

const toEditState = (line: AppointmentReportLine): EditState => ({
  appointmentDate: inputDate(line.appointmentDate),
  patientId: String(line.patientId || ''),
  doctorId: line.doctorId == null ? '' : String(line.doctorId),
  secondDoctorId: line.secondDoctorId == null ? '' : String(line.secondDoctorId),
  treatmentName: line.treatmentName,
  treatedTeeth: line.treatedTeeth || '',
  grossAmount: String(line.grossAmount),
  discountAmount: String(line.discountAmount),
  total: String(line.total),
  invoice: line.invoice,
  specialMaterialsCost: String(line.specialMaterialsCost),
  category: line.category,
  laboratoryCost: String(line.laboratoryCost),
  laboratoryId: line.laboratoryId == null ? '' : String(line.laboratoryId),
  mechanicName: line.mechanicName || '',
  commissionOverride: line.commissionOverride == null ? '' : String(line.commissionOverride),
  secondDoctorCommissionPercentage: String(line.secondDoctorCommissionPercentage || 0),
  payments: line.payments.map(payment => ({ ...payment })),
  useCalculatedOverride: !!line.override,
  overrideEntry: String(line.override?.commissionableAmount ?? line.commissionableAmount),
  overridePrimary: String(line.override?.primaryDoctorShare ?? line.primaryDoctorShare),
  overrideSecond: String(line.override?.secondDoctorShare ?? line.secondDoctorShare),
  overrideClinic: String(line.override?.clinicShare ?? line.clinicShare)
});

export const DoctorTreatmentsPage: React.FC<DoctorTreatmentsPageProps> = ({ doctorId, doctorName, month, onBack }) => {
  const { t, i18n } = useTranslation();
  const theme = useThemedStyles();
  const [report, setReport] = useState<ReportResponse | null>(null);
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [doctors, setDoctors] = useState<DoctorOption[]>([]);
  const [laboratories, setLaboratories] = useState<LaboratoryOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<AppointmentReportLine | null>(null);
  const [form, setForm] = useState<EditState | null>(null);
  const [detailAppointmentId, setDetailAppointmentId] = useState<number | null>(null);

  const range = useMemo(() => {
    const [year, monthNumber] = month.split('-').map(Number);
    const lastDay = new Date(year, monthNumber, 0).getDate();
    return { start: `${month}-01`, end: `${month}-${String(lastDay).padStart(2, '0')}` };
  }, [month]);

  const fetchReport = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token') || '';
      const response = await fetch(
        `${getApiUrl(`${API_CONFIG.ENDPOINTS.REPORTS_DOCTORS}/${doctorId}/appointment-lines`)}?startDate=${range.start}&endDate=${range.end}`,
        { headers: getAuthHeaders(token) }
      );
      if (!response.ok) throw new Error(await response.text() || t('doctorTreatments.loadError'));
      const payload = await response.json();
      setReport({ lines: Array.isArray(payload?.lines) ? payload.lines : [], capabilities: payload?.capabilities || {} });
    } catch (reason) {
      setReport(null);
      setError(reason instanceof Error ? reason.message : t('doctorTreatments.loadError'));
    } finally {
      setLoading(false);
    }
  }, [doctorId, range.end, range.start, t]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  useEffect(() => {
    const token = localStorage.getItem('token') || '';
    Promise.all([
      fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENTS), { headers: getAuthHeaders(token) }).then(value => value.ok ? value.json() : []),
      fetch(getApiUrl(API_CONFIG.ENDPOINTS.DOCTORS), { headers: getAuthHeaders(token) }).then(value => value.ok ? value.json() : []),
      fetch(getApiUrl(API_CONFIG.ENDPOINTS.LABORATORIES), { headers: getAuthHeaders(token) }).then(value => value.ok ? value.json() : [])
    ]).then(([patientRows, doctorRows, laboratoryRows]) => {
      setPatients(Array.isArray(patientRows) ? patientRows : []);
      setDoctors(Array.isArray(doctorRows) ? doctorRows : []);
      setLaboratories(Array.isArray(laboratoryRows) ? laboratoryRows : []);
    }).catch(() => { setPatients([]); setDoctors([]); setLaboratories([]); });
  }, []);

  const lines = report?.lines || [];
  const appointmentCount = new Set(lines.map(line => line.appointmentId)).size;
  const periodCollections = lines.reduce((sum, line) => sum + Number(line.periodPaid || 0), 0);
  const selectedDoctorShare = lines.reduce((sum, line) => sum + Number(
    line.doctorId === doctorId ? line.primaryDoctorShare : line.secondDoctorId === doctorId ? line.secondDoctorShare : 0
  ), 0);

  const openEdit = (line: AppointmentReportLine) => {
    setEditing(line);
    setForm(toEditState(line));
    setError('');
  };

  const changeGross = (value: string) => setForm(current => current ? {
    ...current,
    grossAmount: value,
    total: String(Math.max(0, Number(value || 0) - Number(current.discountAmount || 0)))
  } : current);
  const changeDiscount = (value: string) => setForm(current => current ? {
    ...current,
    discountAmount: value,
    total: String(Math.max(0, Number(current.grossAmount || 0) - Number(value || 0)))
  } : current);
  const changeTotal = (value: string) => setForm(current => current ? {
    ...current,
    total: value,
    discountAmount: String(Math.max(0, Number(current.grossAmount || 0) - Number(value || 0)))
  } : current);

  const updatePayment = (index: number, patch: Partial<EditablePayment>) => setForm(current => current ? {
    ...current,
    payments: current.payments.map((payment, paymentIndex) => paymentIndex === index ? { ...payment, ...patch } : payment)
  } : current);

  const addPayment = () => setForm(current => current ? {
    ...current,
    payments: [...current.payments, {
      date: `${month}-01T12:00:00Z`, amount: 0, method: 'Cash', notes: '', kind: 'payment', inSelectedPeriod: true
    }]
  } : current);

  const translateServerError = (message?: string) => {
    if (!message) return t('doctorTreatments.saveError');
    if (message.includes('Appointment does not exist')) return t('doctorTreatments.errors.notFound');
    if (message.includes('only their own appointment')) return t('doctorTreatments.errors.ownRowsOnly');
    if (message.includes('does not belong to appointment')) return t('doctorTreatments.errors.lineMismatch');
    if (message.includes('require secretary') || message.includes('require administrator') || message.includes('read only'))
      return t('doctorTreatments.errors.accessDenied');
    if (message.includes('period affected by this edit is closed')) return t('doctorTreatments.errors.closedPeriod');
    if (message.includes('Selected patient does not exist')) return t('doctorTreatments.errors.patientNotFound');
    if (message.includes('Payment amount must be positive') || message.includes('Payment method is required'))
      return t('doctorTreatments.errors.paymentInvalid');
    if (message.includes('does not belong to this report line')) return t('doctorTreatments.errors.paymentMismatch');
    if (message.includes('refund workflow')) return t('doctorTreatments.errors.refundWorkflow');
    if (message.includes('override values are required together')) return t('doctorTreatments.errors.overrideIncomplete');
    if (message.includes('override values cannot be negative')) return t('doctorTreatments.errors.overrideNegative');
    if (message.includes('shares must equal Entry')) return t('doctorTreatments.errors.overrideUnbalanced');
    if (message.includes('name is required') || message.includes('reason is required')) return t('doctorTreatments.errors.nameRequired');
    if (message.includes('total must be between zero')) return t('doctorTreatments.errors.totalInvalid');
    if (message.includes('commission percentages must be between')) return t('doctorTreatments.errors.commissionInvalid');
    return message;
  };

  const save = async () => {
    if (!editing || !form) return;
    setSaving(true);
    setError('');
    const capabilities = editing.capabilities || report?.capabilities;
    const numberOrNull = (value: string) => value === '' ? null : Number(value);
    const payload = {
      periodStart: `${month}-01T00:00:00`,
      treatmentId: editing.treatmentId ?? null,
      appointment: capabilities?.canEditScheduling ? {
        patientId: Number(form.patientId),
        appointmentDate: `${form.appointmentDate}T00:00:00`,
        doctorId: numberOrNull(form.doctorId),
        secondDoctorId: numberOrNull(form.secondDoctorId)
      } : null,
      treatment: capabilities?.canEditClinical || capabilities?.canEditScheduling ? {
        treatmentName: form.treatmentName,
        treatedTeeth: form.treatedTeeth || null,
        grossAmount: Number(form.grossAmount),
        total: Number(form.total),
        invoice: form.invoice,
        specialMaterialsCost: Number(form.specialMaterialsCost),
        category: form.category,
        laboratoryCost: Number(form.laboratoryCost),
        laboratoryId: numberOrNull(form.laboratoryId),
        mechanicName: form.mechanicName || null,
        commissionOverride: numberOrNull(form.commissionOverride),
        secondDoctorCommissionPercentage: numberOrNull(form.secondDoctorCommissionPercentage)
      } : null,
      payments: capabilities?.canEditPayments ? form.payments
        .filter(payment => payment.kind.toLowerCase() !== 'refund')
        .map(payment => ({ id: payment.id ?? null, date: payment.date, amount: Number(payment.amount), method: payment.method, notes: payment.notes })) : null,
      calculatedOverride: capabilities?.canOverrideCalculated
        ? form.useCalculatedOverride ? {
          clear: false,
          commissionableAmount: Number(form.overrideEntry),
          primaryDoctorShare: Number(form.overridePrimary),
          secondDoctorShare: Number(form.overrideSecond),
          clinicShare: Number(form.overrideClinic)
        } : editing.override ? { clear: true } : null
        : null
    };
    try {
      const response = await fetch(getApiUrl(`/reports/appointments/${editing.appointmentId}/line`), {
        method: 'PUT',
        headers: getAuthHeaders(localStorage.getItem('token') || ''),
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(translateServerError(body?.error));
      }
      invalidateAppointmentCache();
      setEditing(null);
      setForm(null);
      await fetchReport();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t('doctorTreatments.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const cell = { padding: 10, fontSize: 13, whiteSpace: 'nowrap' as const };
  const fieldStyle = { display: 'grid', gap: 5 } as React.CSSProperties;
  const inputStyle = { ...theme.input, padding: '8px 10px', borderRadius: 6, width: '100%', boxSizing: 'border-box' as const };

  return (
    <div style={{ padding: 24, color: theme.text, background: theme.tokens.app.background, height: '100%', overflow: 'auto' }}>
      <header className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button onClick={onBack} style={{ padding: '8px 16px', ...theme.secondaryButton }}>{t('common.back')}</button>
        <div>
          <h2 style={{ margin: 0 }}>{doctorName} - {t('doctorTreatments.titleSuffix')}</h2>
          <p style={{ margin: '4px 0 0', color: theme.muted }}>{new Date(`${month}-01T12:00:00`).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })}</p>
        </div>
      </header>

      {error && <div role="alert" style={{ marginBottom: 14, padding: 12, borderRadius: 6, background: '#7f1d1d', color: '#fecaca' }}>{error}</div>}

      <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          [t('doctorTreatments.totalAppointments'), appointmentCount],
          [t('doctorTreatments.periodCollections'), money(periodCollections)],
          [t('doctorTreatments.doctorShare'), money(selectedDoctorShare)]
        ].map(([label, value]) => <div key={String(label)} style={{ ...theme.card, padding: 20 }}>
          <div style={{ color: theme.muted, marginBottom: 4 }}>{label}</div><strong style={{ fontSize: 26 }}>{value}</strong>
        </div>)}
      </div>

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: theme.muted }}>{t('doctorTreatments.loading')}</div> :
        <div className="responsive-table-scroll" style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${theme.border}` }}>
          <ResponsiveTable><table style={{ width: '100%', minWidth: 2550, borderCollapse: 'collapse', background: theme.surface }}>
            <thead><tr style={{ background: theme.inputBg, textAlign: 'left' }}>
              {[
                t('common.date'), 'HC', t('common.patient'), t('common.treatment'), t('doctorTreatments.teeth'),
                t('doctorTreatments.grossAmount'), t('doctorTreatments.discount'), t('doctorTreatments.invoice'),
                t('doctorTreatments.account'), t('doctorTreatments.total'), t('reports.balanceDue', 'Balance due'), t('doctorTreatments.periodPaid'),
                t('doctorTreatments.specialMaterials'), t('doctorTreatments.category'), t('doctorTreatments.laboratoryCost'),
                t('doctorTreatments.laboratory'), t('doctorTreatments.mechanic'), t('doctorTreatments.entry'),
                t('reports.doctor'), t('doctorTreatments.assistant'), t('doctorTreatments.commission'),
                t('doctorTreatments.doctorShare'), t('doctorTreatments.clinicShare'), t('doctorTreatments.assistantShare'),
                t('doctorTreatments.actions')
              ].map(label => <th key={label} style={{ ...cell, color: theme.muted }}>{label}</th>)}
            </tr></thead>
            <tbody>
              {lines.map(line => <tr key={`${line.appointmentId}:${line.lineKey}`}
                tabIndex={0}
                onClick={() => setDetailAppointmentId(line.appointmentId)}
                onKeyDown={event => {
                  if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault(); setDetailAppointmentId(line.appointmentId);
                  }
                }}
                style={{ borderTop: `1px solid ${theme.border}`, cursor: 'pointer', background: Number(line.balanceDue || 0) > 0.005 ? 'rgba(239,68,68,0.12)' : undefined }}>
                <td style={cell}>{formatDate(line.appointmentDate)}</td>
                <td style={cell}>{line.patientHc || '-'}</td><td style={cell}>{line.patientName}</td><td style={cell}>{line.treatmentName}</td>
                <td style={cell}>{line.treatedTeeth || '-'}</td><td style={cell}>{money(line.grossAmount)}</td><td style={cell}>{money(line.discountAmount)}</td>
                <td style={cell}>{line.invoice ? t('common.yes') : t('common.no')}</td><td style={cell}>{line.paymentMethods || '-'}</td>
                <td style={cell}>{money(line.total)}</td><td style={{ ...cell, color: Number(line.balanceDue || 0) > 0.005 ? (theme.tokens.isLight ? '#b91c1c' : '#fca5a5') : theme.text, fontWeight: Number(line.balanceDue || 0) > 0.005 ? 700 : undefined }}>{money(line.balanceDue || 0)}</td><td style={cell}>{money(line.periodPaid)}</td><td style={cell}>{money(line.specialMaterialsCost)}</td>
                <td style={cell}>{t(`doctorTreatments.categories.${line.category}`)}</td><td style={cell}>{money(line.laboratoryCost)}</td>
                <td style={cell}>{line.laboratoryName || '-'}</td><td style={cell}>{line.mechanicName || '-'}</td><td style={cell}>{money(line.commissionableAmount)}</td>
                <td style={cell}>{line.doctorName || '-'}</td><td style={cell}>{line.secondDoctorName || '-'}</td><td style={cell}>{line.commissionPercentage.toFixed(2)}%</td>
                <td style={{ ...cell, color: '#60a5fa' }}>{money(line.primaryDoctorShare)}</td><td style={{ ...cell, color: '#34d399' }}>{money(line.clinicShare)}</td>
                <td style={cell}>{line.secondDoctorId ? money(line.secondDoctorShare) : '-'}</td>
                <td style={cell}>{(line.capabilities.canEditClinical || line.capabilities.canEditScheduling || line.capabilities.canEditPayments || line.capabilities.canOverrideCalculated) &&
                  <button onClick={event => { event.stopPropagation(); openEdit(line); }} style={{ ...theme.secondaryButton, padding: '5px 10px' }}>{t('common.edit')}</button>}
                </td>
              </tr>)}
              {lines.length === 0 && <tr><td colSpan={25} style={{ padding: 24, textAlign: 'center', color: theme.muted }}>{t('doctorTreatments.noData')}</td></tr>}
            </tbody>
          </table></ResponsiveTable>
        </div>}

      {detailAppointmentId !== null && <ReportAppointmentDetails appointmentId={detailAppointmentId} onClose={() => setDetailAppointmentId(null)} />}
      {editing && form && <ModalBackdrop style={{ position: 'fixed', inset: 0, zIndex: 2500, background: '#0009' }} onDismiss={() => setEditing(null)} dismissDisabled={saving}>
        <aside style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 'min(760px, 96vw)', overflowY: 'auto', padding: 20, background: theme.surface, color: theme.text, borderLeft: `1px solid ${theme.border}` }} onMouseDown={event => event.stopPropagation()}>
          <h3 style={{ marginTop: 0 }}>{t('doctorTreatments.editAppointmentLine', { id: editing.appointmentId })}</h3>
          <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            {editing.capabilities.canEditScheduling && <>
              <label style={fieldStyle}>{t('common.date')}<BolivianDateInput style={inputStyle} value={form.appointmentDate} onChange={appointmentDate => setForm({ ...form, appointmentDate })} /></label>
              <label style={fieldStyle}>{t('common.patient')}<select style={inputStyle} value={form.patientId} onChange={event => setForm({ ...form, patientId: event.target.value })}>{patients.map(patient => <option key={patient.id} value={patient.id}>{patient.HC || patient.hc || '-'} · {patientLabel(patient)}</option>)}</select></label>
              <label style={fieldStyle}>{t('reports.doctor')}<select style={inputStyle} value={form.doctorId} onChange={event => setForm({ ...form, doctorId: event.target.value })}><option value="">-</option>{doctors.map(doctor => <option key={doctor.id} value={doctor.id}>{doctor.firstName} {doctor.lastName}</option>)}</select></label>
              <label style={fieldStyle}>{t('doctorTreatments.assistant')}<select style={inputStyle} value={form.secondDoctorId} onChange={event => setForm({ ...form, secondDoctorId: event.target.value })}><option value="">-</option>{doctors.map(doctor => <option key={doctor.id} value={doctor.id}>{doctor.firstName} {doctor.lastName}</option>)}</select></label>
            </>}
            {(editing.capabilities.canEditClinical || editing.capabilities.canEditScheduling) && <>
              <label style={{ ...fieldStyle, gridColumn: '1 / -1' }}>{t('common.treatment')}<input style={inputStyle} value={form.treatmentName} onChange={event => setForm({ ...form, treatmentName: event.target.value })} /></label>
              <label style={fieldStyle}>{t('doctorTreatments.teeth')}<input style={inputStyle} value={form.treatedTeeth} onChange={event => setForm({ ...form, treatedTeeth: event.target.value })} /></label>
              <label style={fieldStyle}>{t('doctorTreatments.category')}<select style={inputStyle} value={form.category} onChange={event => setForm({ ...form, category: event.target.value as 'general' | 'specialty' })}><option value="general">{t('doctorTreatments.categories.general')}</option><option value="specialty">{t('doctorTreatments.categories.specialty')}</option></select></label>
            </>}
            {editing.capabilities.canEditScheduling && <>
              <label style={fieldStyle}>{t('doctorTreatments.grossAmount')}<input type="number" min="0" step="0.01" style={inputStyle} value={form.grossAmount} onChange={event => changeGross(event.target.value)} /></label>
              <label style={fieldStyle}>{t('doctorTreatments.discount')}<input type="number" min="0" step="0.01" style={inputStyle} value={form.discountAmount} onChange={event => changeDiscount(event.target.value)} /></label>
              <label style={fieldStyle}>{t('doctorTreatments.total')}<input type="number" min="0" step="0.01" style={inputStyle} value={form.total} onChange={event => changeTotal(event.target.value)} /></label>
              <label style={fieldStyle}>{t('doctorTreatments.invoice')}<input type="checkbox" checked={form.invoice} onChange={event => setForm({ ...form, invoice: event.target.checked })} /></label>
              <label style={fieldStyle}>{t('doctorTreatments.specialMaterials')}<input type="number" min="0" step="0.01" style={inputStyle} value={form.specialMaterialsCost} onChange={event => setForm({ ...form, specialMaterialsCost: event.target.value })} /></label>
              <label style={fieldStyle}>{t('doctorTreatments.laboratory')}<select style={inputStyle} value={form.laboratoryId} onChange={event => setForm({ ...form, laboratoryId: event.target.value })}><option value="">-</option>{laboratories.map(lab => <option key={lab.id} value={lab.id}>{lab.name}</option>)}</select></label>
              <label style={fieldStyle}>{t('doctorTreatments.laboratoryCost')}<input type="number" min="0" step="0.01" style={inputStyle} value={form.laboratoryCost} onChange={event => setForm({ ...form, laboratoryCost: event.target.value })} /></label>
              <label style={fieldStyle}>{t('doctorTreatments.mechanic')}<input style={inputStyle} value={form.mechanicName} onChange={event => setForm({ ...form, mechanicName: event.target.value })} /></label>
              <label style={fieldStyle}>{t('doctorTreatments.commissionOverride')}<input type="number" min="0" max="100" step="0.01" style={inputStyle} value={form.commissionOverride} placeholder={form.category === 'specialty' ? '60' : '50'} onChange={event => setForm({ ...form, commissionOverride: event.target.value })} /></label>
              <label style={fieldStyle}>{t('doctorTreatments.assistantCommission')}<input type="number" min="0" max="100" step="0.01" style={inputStyle} value={form.secondDoctorCommissionPercentage} onChange={event => setForm({ ...form, secondDoctorCommissionPercentage: event.target.value })} /></label>
            </>}
          </div>

          {editing.capabilities.canEditPayments && <section style={{ marginTop: 20 }}>
            <h4>{t('doctorTreatments.paymentLedger')}</h4>
            {form.payments.map((payment, index) => <div className="responsive-grid" key={payment.id ?? `new:${index}`} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1.5fr', gap: 8, marginBottom: 8 }}>
              <BolivianDateInput style={inputStyle} disabled={payment.kind === 'refund'} value={inputDate(payment.date)} onChange={date => updatePayment(index, { date: date ? `${date}T12:00:00Z` : '' })} />
              <input type="number" min="0.01" step="0.01" style={inputStyle} disabled={payment.kind === 'refund'} value={payment.amount} onChange={event => updatePayment(index, { amount: Number(event.target.value) })} />
              <input style={inputStyle} disabled={payment.kind === 'refund'} value={payment.method} onChange={event => updatePayment(index, { method: event.target.value })} />
              <input style={inputStyle} disabled={payment.kind === 'refund'} value={payment.notes} onChange={event => updatePayment(index, { notes: event.target.value })} />
            </div>)}
            <button type="button" style={{ ...theme.secondaryButton, padding: '7px 10px' }} onClick={addPayment}>{t('doctorTreatments.addPayment')}</button>
          </section>}

          {editing.capabilities.canOverrideCalculated && <section style={{ marginTop: 20, padding: 14, border: `1px solid ${theme.border}`, borderRadius: 8 }}>
            <label className="responsive-row" style={{ display: 'flex', gap: 8 }}><input type="checkbox" checked={form.useCalculatedOverride} onChange={event => setForm({ ...form, useCalculatedOverride: event.target.checked })} />{t('doctorTreatments.overrideCalculated')}</label>
            {form.useCalculatedOverride && <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginTop: 12 }}>
              {[[t('doctorTreatments.entry'), 'overrideEntry'], [t('doctorTreatments.doctorShare'), 'overridePrimary'], [t('doctorTreatments.assistantShare'), 'overrideSecond'], [t('doctorTreatments.clinicShare'), 'overrideClinic']].map(([label, key]) =>
                <label key={key} style={fieldStyle}>{label}<input type="number" min="0" step="0.01" style={inputStyle} value={(form as any)[key]} onChange={event => setForm({ ...form, [key]: event.target.value })} /></label>)}
            </div>}
          </section>}

          <div className="responsive-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
            <button disabled={saving} style={{ ...theme.secondaryButton, padding: '9px 15px' }} onClick={() => { setEditing(null); setForm(null); }}>{t('common.cancel')}</button>
            <button disabled={saving} style={{ ...theme.button, padding: '9px 15px' }} onClick={save}>{saving ? t('common.saving') : t('common.save')}</button>
          </div>
        </aside>
      </ModalBackdrop>}
    </div>
  );
};
